<template>
  <div class="product-category">
    <div class="page-header">
      <div>
        <h2 class="page-title">商品分类</h2>
        <p class="page-desc">管理商品分类，支持多门店/多分店专属分类</p>
      </div>
      <el-button type="primary" size="large" @click="openAddDialog">
        <el-icon>
          <Plus />
        </el-icon>
        新增分类
      </el-button>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <el-select v-model="branchFilter" placeholder="请选择门店" clearable filterable style="width: 200px"
        @change="onBranchFilterChange">
        <el-option label="全部门店" :value="null" />
        <el-option v-for="store in storeOptions" :key="store.id" :label="store.store_name" :value="store.id" />
      </el-select>

      <el-select v-model="storeBranchFilter" placeholder="请选择分店" clearable filterable style="width: 200px"
        :disabled="!branchFilter" @change="fetchCategories">
        <el-option label="全部分店" :value="null" />
        <el-option v-for="branch in branchOptions" :key="branch.id" :label="branch.branch_name" :value="branch.id" />
      </el-select>

      <el-button type="primary" @click="fetchCategories">查询</el-button>
      <el-button @click="resetFilter">重置</el-button>

      <div class="filter-tip">
        <el-tag type="info" size="small">选择门店后，新增的分类将属于该门店</el-tag>
        <el-tag type="success" size="small" style="margin-left: 8px;">选择分店后，新增的分类将专属于该分店</el-tag>
      </div>
    </div>

    <!-- 分类列表 -->
    <div class="category-list">
      <div v-for="category in categories" :key="category.id" class="category-item">
        <div class="category-info">
          <div class="category-icon">
            <el-icon :size="32" color="#409eff">
              <Folder />
            </el-icon>
          </div>
          <div class="category-detail">
            <div class="category-name">{{ category.category_name }}</div>
            <div class="category-meta">
              <span>排序: {{ category.sort_order }}</span>
              <span>状态:
                <el-tag :type="category.status === 1 ? 'success' : 'danger'" size="small">
                  {{ category.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </span>
              <!-- 门店专属 -->
              <span v-if="category.branch_id && !category.store_branch_id" class="scope-tag">
                <el-tag type="primary" size="small">
                  门店: {{ getStoreName(category.branch_id) }}
                </el-tag>
              </span>
              <!-- 分店专属：同时显示门店和分店 -->
              <span v-if="category.store_branch_id" class="scope-tag">
                <el-tag type="warning" size="small">
                  门店: {{ getStoreNameByBranchId(category.store_branch_id) }} / 分店: {{
                    getBranchName(category.store_branch_id) }}
                </el-tag>
              </span>
              <!-- 全局分类 -->
              <span v-if="!category.branch_id && !category.store_branch_id" class="scope-tag">
                <el-tag type="info" size="small">全局分类</el-tag>
              </span>
            </div>
          </div>
        </div>
        <div class="category-actions">
          <el-button type="primary" link @click="openEditDialog(category)">
            <el-icon>
              <Edit />
            </el-icon> 编辑
          </el-button>
          <el-button type="success" link @click="copyCategory(category)">
            <el-icon>
              <CopyDocument />
            </el-icon> 复制
          </el-button>
          <el-popconfirm title="确定删除该分类吗？" @confirm="handleDelete(category.id)">
            <template #reference>
              <el-button type="danger" link>
                <el-icon>
                  <Delete />
                </el-icon> 删除
              </el-button>
            </template>
          </el-popconfirm>
        </div>
      </div>

      <div v-if="categories.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无分类，点击右上角新增" />
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="550px" destroy-on-close>
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <el-form-item label="分类名称" prop="category_name" required>
          <el-input v-model="formData.category_name" placeholder="请输入分类名称" />
        </el-form-item>

        <el-form-item label="适用范围">
          <el-radio-group v-model="scopeType" @change="onScopeChange">
            <el-radio :label="1">全局分类（全部门店/分店可用）</el-radio>
            <el-radio :label="2">门店专属（仅该门店及旗下分店可用）</el-radio>
            <el-radio :label="3">分店专属（仅该分店可用）</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="选择门店" v-if="scopeType === 2 || scopeType === 3">
          <el-select v-model="formData.branch_id" placeholder="请选择门店" filterable style="width: 100%"
            @change="onBranchChange">
            <el-option v-for="store in storeOptions" :key="store.id" :label="store.store_name" :value="store.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="选择分店" v-if="scopeType === 3 && formData.branch_id">
          <el-select v-model="formData.store_branch_id" placeholder="请选择分店" filterable style="width: 100%">
            <el-option v-for="branch in branchOptions" :key="branch.id" :label="branch.branch_name"
              :value="branch.id" />
          </el-select>
          <div v-if="branchOptions.length === 0" class="form-tip">该门店下暂无分店，请先添加分店</div>
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
        </el-form-item>

        <el-form-item label="状态">
          <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="启用"
            inactive-text="禁用" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { Plus, Edit, Delete, Folder, CopyDocument } from '@element-plus/icons-vue'

export default {
  name: 'ProductCategoryPage',
  components: { Plus, Edit, Delete, Folder, CopyDocument },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)

    const loading = ref(false)
    const submitting = ref(false)
    const dialogVisible = ref(false)
    const isEditMode = ref(false)
    const isCopyMode = ref(false)
    const categories = ref([])
    const storeOptions = ref([])
    const branchOptions = ref([])
    const branchFilter = ref(null)
    const storeBranchFilter = ref(null)
    const scopeType = ref(1)
    const formRef = ref(null)

    const formData = reactive({
      id: null,
      category_name: '',
      sort_order: 0,
      status: 1,
      branch_id: null,
      store_branch_id: null
    })

    const formRules = {
      category_name: [{ required: true, message: '请输入分类名称', trigger: 'blur' }]
    }

    const dialogTitle = computed(() => {
      if (isCopyMode.value) return '复制分类'
      return isEditMode.value ? '编辑分类' : '新增分类'
    })


    // 获取门店名称
    const getStoreName = (branchId) => {
      const store = storeOptions.value.find(s => s.id === branchId)
      return store ? store.store_name : '未知门店'
    }

    // 获取分店名称
    const getBranchName = (storeBranchId) => {
      const branch = allBranchOptions.value.find(b => b.id === storeBranchId)
      return branch ? branch.branch_name : '未知分店'
    }

    // 根据分店ID获取所属门店ID和门店名称
    const getStoreNameByBranchId = (storeBranchId) => {
      const branch = allBranchOptions.value.find(b => b.id === storeBranchId)
      if (branch) {
        // 通过分店的 branch_id 找到门店名称
        const store = storeOptions.value.find(s => s.id === branch.branch_id)
        return store ? store.store_name : '未知门店'
      }
      return '未知门店'
    }

    // 需要维护一个全部分店的列表
    const allBranchOptions = ref([])

    // 获取全部分店列表
    const fetchAllBranches = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          allBranchOptions.value = response.data.data.list || []
        }
      } catch (error) {
        console.error('获取分店失败:', error)
      }
    }

    // 获取门店列表
    const fetchStores = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          storeOptions.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取门店失败:', error)
      }
    }

    // 获取分店列表
    const fetchBranches = async (branchId) => {
      if (!branchId) {
        branchOptions.value = []
        return
      }
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
          params: {
            agent_id: agentId.value,
            store_id: storeId.value,
            branch_id: branchId,
            limit: 100
          }
        })
        if (response.data.code === 200) {
          branchOptions.value = response.data.data.list || []
        } else {
          branchOptions.value = []
        }
      } catch (error) {
        console.error('获取分店失败:', error)
        branchOptions.value = []
      }
    }

    // 获取分类列表
    const fetchCategories = async () => {
      loading.value = true
      try {
        const params = {
          agent_id: agentId.value,
          store_id: storeId.value,
          type: 'product'
        }

        // 只有在选择了门店或分店时才传递筛选参数
        if (branchFilter.value) {
          params.branch_id = branchFilter.value
        }
        if (storeBranchFilter.value) {
          params.store_branch_id = storeBranchFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/category_api.php`, { params })
        if (response.data.code === 200) {
          categories.value = response.data.data
        }
      } catch (error) {
        console.error('获取分类失败:', error)
        ElMessage.error('获取分类失败')
      } finally {
        loading.value = false
      }
    }

    // 门店筛选变化
    const onBranchFilterChange = async () => {
      // 清空分店筛选
      storeBranchFilter.value = null
      // 如果选择了门店，加载该门店下的分店
      if (branchFilter.value) {
        await fetchBranches(branchFilter.value)
      } else {
        branchOptions.value = []
      }
      // 重新获取分类
      await fetchCategories()
    }

    // 重置筛选
    const resetFilter = () => {
      branchFilter.value = null
      storeBranchFilter.value = null
      branchOptions.value = []
      fetchCategories()
    }

    // 打开新增弹窗
    const openAddDialog = () => {
      isEditMode.value = false
      isCopyMode.value = false
      scopeType.value = 1
      resetFormData()
      dialogVisible.value = true
    }

    // 重置表单数据
    const resetFormData = () => {
      Object.assign(formData, {
        id: null,
        category_name: '',
        sort_order: 0,
        status: 1,
        branch_id: null,
        store_branch_id: null
      })
      branchOptions.value = []
    }

    // 打开编辑弹窗
    const openEditDialog = async (category) => {
      isEditMode.value = true
      isCopyMode.value = false

      // 设置适用范围类型
      if (category.store_branch_id) {
        scopeType.value = 3
      } else if (category.branch_id) {
        scopeType.value = 2
      } else {
        scopeType.value = 1
      }

      // 填充表单数据
      Object.assign(formData, {
        id: category.id,
        category_name: category.category_name,
        sort_order: category.sort_order,
        status: category.status,
        branch_id: category.branch_id,
        store_branch_id: category.store_branch_id
      })

      // 如果有关联门店，加载分店列表
      if (category.branch_id) {
        await fetchBranches(category.branch_id)
      }

      dialogVisible.value = true
    }

    // 复制分类
    const copyCategory = async (category) => {
      isCopyMode.value = true
      isEditMode.value = false

      // 设置适用范围类型
      if (category.store_branch_id) {
        scopeType.value = 3
      } else if (category.branch_id) {
        scopeType.value = 2
      } else {
        scopeType.value = 1
      }

      // 填充表单数据（复制原分类数据，但清空ID）
      Object.assign(formData, {
        id: null,
        category_name: `${category.category_name}(副本)`,
        sort_order: category.sort_order + 1,
        status: category.status,
        branch_id: category.branch_id,
        store_branch_id: category.store_branch_id
      })

      // 如果有关联门店，加载分店列表
      if (category.branch_id) {
        await fetchBranches(category.branch_id)
      }

      dialogVisible.value = true
    }

    // 适用范围变化
    const onScopeChange = () => {
      if (scopeType.value === 1) {
        formData.branch_id = null
        formData.store_branch_id = null
        branchOptions.value = []
      } else if (scopeType.value === 2) {
        formData.store_branch_id = null
        if (!formData.branch_id && storeOptions.value.length > 0) {
          formData.branch_id = storeOptions.value[0].id
          fetchBranches(formData.branch_id)
        } else if (formData.branch_id) {
          fetchBranches(formData.branch_id)
        }
      } else if (scopeType.value === 3) {
        if (!formData.branch_id && storeOptions.value.length > 0) {
          formData.branch_id = storeOptions.value[0].id
          fetchBranches(formData.branch_id)
        } else if (formData.branch_id) {
          fetchBranches(formData.branch_id)
        }
      }
    }

    // 门店变化
    const onBranchChange = () => {
      formData.store_branch_id = null
      fetchBranches(formData.branch_id)
    }

    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return

      await formRef.value.validate(async (valid) => {
        if (!valid) return

        // 验证：如果选择分店专属，必须选择分店
        if (scopeType.value === 3 && !formData.store_branch_id) {
          ElMessage.warning('请选择分店')
          return
        }

        // 验证：如果选择门店专属，必须选择门店
        if (scopeType.value === 2 && !formData.branch_id) {
          ElMessage.warning('请选择门店')
          return
        }

        submitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          const payload = {
            agent_id: agentId.value,
            store_id: storeId.value,
            category_name: formData.category_name,
            sort_order: formData.sort_order,
            status: formData.status,
            type: 'product',
            branch_id: scopeType.value >= 2 ? formData.branch_id : null,
            store_branch_id: scopeType.value === 3 ? formData.store_branch_id : null
          }

          let response
          if (isEditMode.value && !isCopyMode.value) {
            payload.id = formData.id
            response = await axios.put(`${config.BASE_URL}/shop/api/category_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/category_api.php`, payload)
          }

          if (response.data.code === 200) {
            const message = isCopyMode.value ? '复制成功' : (isEditMode.value ? '更新成功' : '新增成功')
            ElMessage.success(message)
            dialogVisible.value = false
            await fetchCategories()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败，请稍后重试')
        } finally {
          submitting.value = false
          loadingInstance.close()
        }
      })
    }

    // 删除分类
    const handleDelete = async (id) => {
      const loadingInstance = ElLoading.service({ text: '删除中...' })
      try {
        const response = await axios.delete(`${config.BASE_URL}/shop/api/category_api.php`, {
          data: { id, store_id: storeId.value }
        })
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          await fetchCategories()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        ElMessage.error('删除失败，请稍后重试')
      } finally {
        loadingInstance.close()
      }
    }

    onMounted(() => {
      fetchStores()
      fetchAllBranches()  // 新增
      fetchCategories()
    })

    return {
      loading,
      submitting,
      dialogVisible,
      isEditMode,
      categories,
      storeOptions,
      branchOptions,
      branchFilter,
      storeBranchFilter,
      scopeType,
      formData,
      formRef,
      formRules,
      dialogTitle,
      openAddDialog,
      openEditDialog,
      copyCategory,
      submitForm,
      handleDelete,
      onScopeChange,
      onBranchChange,
      onBranchFilterChange,
      resetFilter,
      getStoreName,
      getBranchName,
      getStoreNameByBranchId,
      fetchCategories
    }
  }
}
</script>

<style scoped lang="scss">
.product-category {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .filter-bar {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f5f7fa;
    border-radius: 12px;

    .filter-tip {
      margin-left: auto;
    }
  }

  .category-list {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .category-item {
      background: #fff;
      border-radius: 12px;
      padding: 16px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .category-info {
        display: flex;
        align-items: center;
        gap: 16px;

        .category-icon {
          width: 48px;
          height: 48px;
          background: #e8f3ff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .category-detail {
          .category-name {
            font-size: 16px;
            font-weight: 500;
            color: #1a1a2e;
            margin-bottom: 6px;
          }

          .category-meta {
            display: flex;
            gap: 16px;
            font-size: 12px;
            color: #8c8c8c;
            align-items: center;
            flex-wrap: wrap;

            .scope-tag {
              margin-left: 4px;
            }
          }
        }
      }

      .category-actions {
        display: flex;
        gap: 12px;
      }
    }
  }

  .empty-state {
    padding: 60px 0;
  }

  .form-tip {
    font-size: 12px;
    color: #e6a23c;
    margin-top: 4px;
  }
}
</style>